import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Theme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Layout } from 'ui/layout/Layout'
import { AppBarAdmin } from 'ui/partials/AppBar'
import AdminContentWrapper from 'ui/layout/AdminContentWrapper'
import AdminSidebar, {
  AdminSidebarSelectRouteProps,
} from 'ui/partials/AdminSidebar'
import type { MedicalInstitutionProps, PublicInformation } from 'types/med'
import { StackItem } from 'ui/layout/StackItem'
import { H2 } from 'ui/shorthands/Texts'
import Section from 'ui/layout/Section'
import SidebarReservationList, {
  SidebarReservationListProps,
} from 'ui/pages/med/Reservations/SidebarReservationList'
import SettingSubmenu, {
  SettingsSubmenuProps,
} from 'ui/pages/med/Setting/SettingSubmenu'
import { InformationToMed, UpdatePasswordParams } from 'types/med'
import PublicInformationSection from './PublicInformationSection'
import AdminUserSection, { AdminUserSectionProps } from './AdminUserSection'
import PublicInformationModal, {
  PublicInformationModalProps,
} from './PublicInformationModal'

const useStyles = makeStyles()((theme: Theme) => ({
  content: {
    paddingTop: theme.spacing(1.5),
  },
  footerControlBar: {
    flex: 1,
    height: '68px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: theme.spacing(2),
    boxSizing: 'border-box',
  },
  adminUserSection: {
    marginTop: theme.spacing(3.5),
  },
  buttonHeader: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

export type OnSubmitPasswordChange = (
  params: UpdatePasswordParams,
  onSuccess: () => void
) => Promise<void>

export type AccountSettingProps = {
  /** 医療機関設定 */
  med: MedicalInstitutionProps

  /** 医療機関公開情報 */
  publicInformation?: PublicInformation

  /** サイドバーでのページ切り替え */
  selectRoute: AdminSidebarSelectRouteProps

  /** サイドバーでのページ切り替え*/
  settingSubmenu?: SettingsSubmenuProps

  /** サイドバーでのページ切り替え*/
  sidebarReservationListProps?: SidebarReservationListProps

  /** パスワード保存時のハンドラ*/
  onSubmitPasswordChange: AdminUserSectionProps['onSubmit']

  /** 公開情報保存時のハンドラ*/
  onSubmitSavePublicInformation: PublicInformationModalProps['onSubmit']

  /** ログアウトクリック時のハンドラ*/
  onLogoutClick: () => void

  update?: InformationToMed
}

const AccountSetting: React.FC<AccountSettingProps> = ({
  med,
  publicInformation,
  selectRoute,
  onSubmitPasswordChange,
  onSubmitSavePublicInformation,
  onLogoutClick,
  settingSubmenu,
  sidebarReservationListProps,
  update,
}: AccountSettingProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const [openPublicInformationModal, setOpenPublicInformationModal] =
    useState<boolean>(false)
  return (
    <Layout header={<AppBarAdmin update={update} />}>
      <AdminContentWrapper
        sidebar={
          <AdminSidebar med={med} selectRoute={selectRoute} headerActive={true}>
            {selectRoute.route === 'reservations'
              ? sidebarReservationListProps && (
                  <SidebarReservationList {...sidebarReservationListProps} />
                )
              : settingSubmenu && <SettingSubmenu {...settingSubmenu} />}
          </AdminSidebar>
        }
        controlBar={
          <div className={classes.footerControlBar}>
            <Button color="primary" onClick={onLogoutClick}>
              {t('ログアウト')}
            </Button>
          </div>
        }
      >
        <div className={classes.content}>
          <H2>{t('施設')}</H2>
          <Section>
            <StackItem label={t('施設名')}>{med.name}</StackItem>
            <StackItem label={t('電話番号')}>{med.phone}</StackItem>
            <StackItem label={t('郵便番号')}>{med.postal_code}</StackItem>
            <StackItem label={t('都道府県')}>{med.prefecture}</StackItem>
            <StackItem label={t('市区町村')}>{med.city}</StackItem>
            <StackItem label={t('町域')}>{med.town}</StackItem>
            <StackItem label={t('丁目・字通り名')}>{med.street}</StackItem>
            <StackItem label={t('建物名')}>{med.building}</StackItem>
          </Section>
          {med.admin_user && (
            <div className={classes.adminUserSection}>
              <H2>{t('管理者')}</H2>
              <Section>
                <AdminUserSection
                  adminUser={med.admin_user}
                  onSubmit={onSubmitPasswordChange}
                />
              </Section>
            </div>
          )}
          <div className={classes.buttonHeader}>
            <H2>{t('インテグリティ・ヘルスケア社ウェブサイトへの掲載')}</H2>
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={() => setOpenPublicInformationModal(true)}
            >
              {t('変更')}
            </Button>
            {openPublicInformationModal && (
              <PublicInformationModal
                open={openPublicInformationModal}
                med={med}
                defaultValues={publicInformation}
                onSubmit={onSubmitSavePublicInformation}
                onCancel={() => setOpenPublicInformationModal(false)}
              />
            )}
          </div>
          <Section>
            <PublicInformationSection
              med={med}
              publicInformation={publicInformation}
            />
          </Section>
        </div>
      </AdminContentWrapper>
    </Layout>
  )
}
export default AccountSetting
